import { apiRequest } from './client.js';

/**
 * Branding (M01 §7 white-label + §16 asset upload) — real /v1/branding contract.
 *
 * Tenant is derived from the caller's session on the backend, so no tenantId is
 * passed here. Backing store is the T003 branding configuration (all 28 editable
 * fields). Assets (logo/favicon/etc.) are uploaded as base64 and referenced by an
 * opaque objectKey persisted on the branding record.
 */

/** Active white-label configuration as returned by GET /v1/branding. */
export interface BrandingConfig {
  readonly id?: string;
  readonly tenantId?: string;
  readonly themeId?: string;
  readonly version?: number;
  // Identity
  readonly applicationName?: string | null;
  readonly shortName?: string | null;
  readonly tagline?: string | null;
  readonly website?: string | null;
  // Contact
  readonly supportEmail?: string | null;
  readonly supportMobile?: string | null;
  // Palette
  readonly primaryColor?: string | null;
  readonly secondaryColor?: string | null;
  readonly accentColor?: string | null;
  // Asset object keys
  readonly logoObjectKey?: string | null;
  readonly darkLogoObjectKey?: string | null;
  readonly mobileLogoObjectKey?: string | null;
  readonly faviconObjectKey?: string | null;
  readonly splashScreenObjectKey?: string | null;
  readonly loginBannerObjectKey?: string | null;
  readonly emailHeaderLogoObjectKey?: string | null;
  readonly emailFooterLogoObjectKey?: string | null;
  readonly pdfHeaderLogoObjectKey?: string | null;
  readonly pdfFooterLogoObjectKey?: string | null;
  // App store / browser / SEO
  readonly androidAppName?: string | null;
  readonly iosAppName?: string | null;
  readonly browserTitle?: string | null;
  readonly metaDescription?: string | null;
  readonly metaKeywords?: string | null;
  // Legal
  readonly copyrightText?: string | null;
  readonly termsUrl?: string | null;
  readonly privacyUrl?: string | null;
  readonly contrastAcknowledgedAt?: string | null;
}

/** Editable subset sent on PUT /v1/branding. All optional; version enables
 *  optimistic concurrency, contrastAcknowledgedAt overrides the WCAG gate. */
export type BrandingUpdateInput = Partial<
  Omit<BrandingConfig, 'id' | 'tenantId'>
>;

/** Uploadable branding asset slots (§16). `assetType` is the API path segment;
 *  `field` is the BrandingConfig objectKey it populates. */
export const BRANDING_ASSETS = [
  { assetType: 'logo', field: 'logoObjectKey', label: 'Logo (light)' },
  { assetType: 'dark_logo', field: 'darkLogoObjectKey', label: 'Logo (dark)' },
  { assetType: 'mobile_logo', field: 'mobileLogoObjectKey', label: 'Mobile logo' },
  { assetType: 'favicon', field: 'faviconObjectKey', label: 'Favicon' },
  { assetType: 'splash_screen', field: 'splashScreenObjectKey', label: 'Splash screen' },
  { assetType: 'login_banner', field: 'loginBannerObjectKey', label: 'Login banner' },
  { assetType: 'email_header_logo', field: 'emailHeaderLogoObjectKey', label: 'Email header logo' },
  { assetType: 'email_footer_logo', field: 'emailFooterLogoObjectKey', label: 'Email footer logo' },
  { assetType: 'pdf_header_logo', field: 'pdfHeaderLogoObjectKey', label: 'PDF header logo' },
  { assetType: 'pdf_footer_logo', field: 'pdfFooterLogoObjectKey', label: 'PDF footer logo' },
] as const;

export type BrandingAssetType = (typeof BRANDING_ASSETS)[number]['assetType'];
export type BrandingAssetField = (typeof BRANDING_ASSETS)[number]['field'];

export interface UploadAssetResult {
  readonly objectKey: string;
  readonly assetType: string;
}

type Tok = string | null | undefined;

/** GET /v1/branding — active branding for the current tenant (null if unset). */
export function getBranding(token?: Tok): Promise<BrandingConfig | null> {
  return apiRequest<BrandingConfig | null>('/v1/branding', { token });
}

/** PUT /v1/branding — upsert white-label fields (returns the saved record). */
export function updateBranding(
  body: BrandingUpdateInput,
  token?: Tok,
): Promise<BrandingConfig> {
  return apiRequest<BrandingConfig>('/v1/branding', { method: 'PUT', token, body });
}

/** POST /v1/branding/upload/:assetType — upload a base64 asset, get its objectKey. */
export function uploadBrandingAsset(
  assetType: string,
  base64Content: string,
  mimeType: string,
  token?: Tok,
): Promise<UploadAssetResult> {
  return apiRequest<UploadAssetResult>(
    `/v1/branding/upload/${encodeURIComponent(assetType)}`,
    { method: 'POST', token, body: { base64Content, mimeType } },
  );
}
